const FREQUENCY_ORDER = [
  "One time",
  "1 week",
  "2 weeks",
  "4 weeks",
  "6 weeks",
  "8 weeks",
  "12 weeks",
];
const SUBSCRIPTION_FREQUENCIES = FREQUENCY_ORDER.slice(1);
const BUNDLE_DRAFT_STORAGE_KEY = "evermoreBundleDraft";
const LOOP_BUNDLE_TRANSACTION_URL =
  "https://bundle.loopwork.co/api/transactions/create";
const LOOP_BUNDLE_CONFIG = {
  bundleId: 569,
  bundleName: "Build Your Pet Food Order",
  bundleVariantId: 1508,
  discountTiers: [
    { threshold: 8, discountId: 1616, code: "LPBNDL4zhkb" },
    { threshold: 16, discountId: 1617, code: "LPBNDL8uin4" },
    { threshold: 32, discountId: 12925, code: "LPBNDLfud70" },
  ],
  sellingPlanIds: {
    "1 week": 22762,
    "2 weeks": 22763,
    "4 weeks": 22765,
    "6 weeks": 22767,
    "8 weeks": 22769,
    "12 weeks": 22773,
  },
};

function canonicalFrequency(value) {
  if (!value) return null;

  const normalized = `${value}`.toLowerCase().trim();
  if (normalized === "one time" || normalized === "one-time") return "One time";

  const weekMatch = normalized.match(/\b(\d+)\s*weeks?\b/);
  const dayMatch = normalized.match(/\b(\d+)\s*days?\b/);
  const noNumberWeekly =
    normalized === "week" ||
    /\bweekly\b/.test(normalized) ||
    /\bevery\s+weeks?\b/.test(normalized) ||
    /\beach\s+week\b/.test(normalized) ||
    /\bper\s+week\b/.test(normalized) ||
    /\bonce\s+a\s+week\b/.test(normalized);

  const weekCount =
    weekMatch?.[1] ||
    (dayMatch && Number(dayMatch[1]) % 7 === 0
      ? `${Number(dayMatch[1]) / 7}`
      : null);
  if (weekCount) {
    return (
      FREQUENCY_ORDER.slice(1).find(
        (option) => option.split(" ")[0] === weekCount,
      ) || null
    );
  }

  return noNumberWeekly ? "1 week" : null;
}

function formatMoney(cents) {
  if (typeof window.formatMoney === "function" && window.theme?.moneyFormat) {
    return window.formatMoney(cents, window.theme.moneyFormat);
  }

  return `$${(Number(cents || 0) / 100).toFixed(2)}`;
}

function getTier(totalCount, tiers) {
  return [...tiers]
    .sort((left, right) => left.threshold - right.threshold)
    .reduce(
      (current, tier) => (totalCount >= tier.threshold ? tier : current),
      null,
    );
}

function getLoopDiscountTier(totalCount) {
  const quantity = Number(totalCount || 0);

  return [...LOOP_BUNDLE_CONFIG.discountTiers]
    .sort((left, right) => right.threshold - left.threshold)
    .find((tier) => quantity >= tier.threshold) || null;
}

function getLoopDiscountId(totalCount) {
  return getLoopDiscountTier(totalCount)?.discountId || null;
}

function getLoopDiscountCode(totalCount) {
  return getLoopDiscountTier(totalCount)?.code || null;
}

async function createLoopBundleTransaction(totalCount, frequency) {
  const sellingPlanId = LOOP_BUNDLE_CONFIG.sellingPlanIds[frequency];
  const bundleDiscountId = getLoopDiscountId(totalCount);

  if (!bundleDiscountId || (frequency && !sellingPlanId)) return null;

  const payload = {
    bundleId: LOOP_BUNDLE_CONFIG.bundleId,
    bundleVariantId: LOOP_BUNDLE_CONFIG.bundleVariantId,
    bundleDiscountId,
    productsQuantity: Number(totalCount || 0),
  };

  if (sellingPlanId) {
    payload.sellingPlanId = sellingPlanId;
  }

  const response = await fetch(LOOP_BUNDLE_TRANSACTION_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json();
  if (!response.ok || !data?.data?.loopBundleGuid) {
    throw new Error(
      data?.message || "Unable to create the Loop bundle transaction.",
    );
  }

  return data.data.loopBundleGuid;
}

function applyDiscount(price, discountPercent) {
  if (!discountPercent) return Number(price || 0);
  return Math.round(Number(price || 0) * ((100 - discountPercent) / 100));
}

function buildSellingPlanMap(product) {
  return (product.sellingPlans || []).reduce((plans, sellingPlan) => {
    const label =
      canonicalFrequency(sellingPlan.optionValue) ||
      canonicalFrequency(sellingPlan.name);
    if (label) plans.set(label, sellingPlan);
    return plans;
  }, new Map());
}

function createThresholdMarkup(totalCount, tiers) {
  return tiers
    .map((tier) => {
      const classes = ["bundle-builder-threshold"];
      if (totalCount >= tier.threshold) {
        classes.push("is-active");
      }

      return `<div class="${classes.join(" ")}">${tier.threshold}+<br>${tier.discount}% off</div>`;
    })
    .join("");
}

function setCardSlide(card, slideIndex) {
  if (!card) return;

  const normalizedIndex = `${slideIndex}`;
  card.dataset.activeSlide = normalizedIndex;
  card.querySelectorAll("[data-bundle-card-dot]").forEach((dot) => {
    dot.classList.toggle(
      "is-active",
      dot.dataset.slideIndex === normalizedIndex,
    );
  });
}

function readBundleDraft() {
  try {
    const draft = JSON.parse(
      window.sessionStorage.getItem(BUNDLE_DRAFT_STORAGE_KEY) || "{}",
    );
    return draft && typeof draft === "object" ? draft : {};
  } catch (error) {
    return {};
  }
}

function writeBundleDraft(items) {
  const draft = {};

  items.forEach((quantity, variantId) => {
    if (quantity > 0) {
      draft[variantId] = quantity;
    }
  });

  window.sessionStorage.setItem(
    BUNDLE_DRAFT_STORAGE_KEY,
    JSON.stringify(draft),
  );
}

function getDraftItems(draft) {
  return draft.items && typeof draft.items === "object" ? draft.items : draft;
}

function writeSplitBundleDraft(subscriptionItems, oneTimeItems, frequency, editKeys = []) {
  const subscriptionDraft = {};
  const oneTimeDraft = {};

  subscriptionItems.forEach((quantity, variantId) => {
    if (quantity > 0) {
      subscriptionDraft[variantId] = quantity;
    }
  });

  oneTimeItems.forEach((quantity, variantId) => {
    if (quantity > 0) {
      oneTimeDraft[variantId] = quantity;
    }
  });

  const draft = {
    items: subscriptionDraft,
    oneTimeItems: oneTimeDraft,
    frequency,
  };

  if (editKeys.length) {
    draft.edit = {
      keys: editKeys,
    };
  }

  window.sessionStorage.setItem(BUNDLE_DRAFT_STORAGE_KEY, JSON.stringify(draft));
}

function clearBundleDraft() {
  window.sessionStorage.removeItem(BUNDLE_DRAFT_STORAGE_KEY);
}

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll("[data-bundle-builder-root]").forEach((root) => {
    const config = JSON.parse(root.dataset.bundleConfig || "{}");
    const products = [...root.querySelectorAll("[data-bundle-product-card]")]
      .map((card) => card.dataset.productJson)
      .filter(Boolean)
      .map((value) => JSON.parse(value));
    const productMap = new Map(
      products.map((product) => [
        product.handle,
        {
          ...product,
          sellingPlanMap: buildSellingPlanMap(product),
        },
      ]),
    );
    const variantMap = new Map(
      products.map((product) => [`${product.variantId}`, product]),
    );

    const summaryItems = root.querySelector("[data-bundle-summary-items]");
    const message = root.querySelector("[data-bundle-message]");
    const oneTimeButton = root.querySelector("[data-bundle-submit-onetime]");
    const totalCount = root.querySelector("[data-bundle-total-count]");
    const subtotal = root.querySelector("[data-bundle-subtotal]");
    const subtotalPrice = root.querySelector("[data-bundle-subtotal-price]");
    const thresholdContainer = root.querySelector("[data-bundle-thresholds]");
    const frequencySelect = root.querySelector(
      "[data-bundle-frequency-select]",
    );
    const cards = new Map(
      [...root.querySelectorAll("[data-bundle-product-card]")].map((card) => [
        card.dataset.productHandle,
        card,
      ]),
    );

    const state = {
      subscriptionItems: new Map(),
      oneTimeItems: new Map(),
      subscriptionFrequency: "",
      editKeys: [],
      loading: false,
      mode: "onetime",
    };

    function getSelectedProducts() {
      const handles = new Set([
        ...state.subscriptionItems.keys(),
        ...state.oneTimeItems.keys(),
      ]);

      return [...handles]
        .map((handle) => ({
          product: productMap.get(handle),
          subscriptionQuantity: state.subscriptionItems.get(handle) || 0,
          oneTimeQuantity: state.oneTimeItems.get(handle) || 0,
        }))
        .map((entry) => ({
          ...entry,
          quantity: entry.subscriptionQuantity + entry.oneTimeQuantity,
        }))
        .filter((entry) => entry.product && entry.quantity > 0);
    }

    function saveDraft() {
      const subscriptionDraftItems = new Map();
      const oneTimeDraftItems = new Map();

      state.subscriptionItems.forEach((quantity, handle) => {
        const product = productMap.get(handle);
        if (product) {
          subscriptionDraftItems.set(`${product.variantId}`, quantity);
        }
      });

      state.oneTimeItems.forEach((quantity, handle) => {
        const product = productMap.get(handle);
        if (product) {
          oneTimeDraftItems.set(`${product.variantId}`, quantity);
        }
      });

      writeSplitBundleDraft(
        subscriptionDraftItems,
        oneTimeDraftItems,
        state.subscriptionFrequency,
        state.editKeys,
      );
    }

    function hydrateDraft() {
      const draft = readBundleDraft();
      const draftItems = getDraftItems(draft);
      const oneTimeDraftItems =
        draft.oneTimeItems && typeof draft.oneTimeItems === "object"
          ? draft.oneTimeItems
          : {};

      Object.entries(draftItems).forEach(([variantId, quantity]) => {
        const product = variantMap.get(`${variantId}`);
        const nextQuantity = Number(quantity || 0);

        if (product && nextQuantity > 0) {
          state.subscriptionItems.set(product.handle, nextQuantity);
        }
      });

      Object.entries(oneTimeDraftItems).forEach(([variantId, quantity]) => {
        const product = variantMap.get(`${variantId}`);
        const nextQuantity = Number(quantity || 0);

        if (product && nextQuantity > 0) {
          state.oneTimeItems.set(product.handle, nextQuantity);
        }
      });

      if (typeof draft.frequency === "string") {
        state.subscriptionFrequency = canonicalFrequency(draft.frequency) || "";
      }

      if (Array.isArray(draft.edit?.keys)) {
        state.editKeys = [...new Set(draft.edit.keys.filter(Boolean))];
      }
    }

    function mergeIncomingBundleItem() {
      const params = new URLSearchParams(window.location.search);
      const variantId = params.get("bundle_variant");
      const quantity = Math.max(1, Number(params.get("bundle_qty") || 1));
      if (!variantId) return null;

      const product = variantMap.get(`${variantId}`);
      let result = null;

      if (product) {
        state.subscriptionItems.set(
          product.handle,
          (state.subscriptionItems.get(product.handle) || 0) + quantity,
        );
        saveDraft();
        result = {
          text: `${product.title} added to your bundle.`,
          tone: "success",
        };
      } else {
        result = {
          text: "That product is not available in this bundle builder.",
          tone: "error",
        };
      }

      params.delete("bundle_variant");
      params.delete("bundle_qty");

      const nextUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""}${window.location.hash}`;
      window.history.replaceState({}, "", nextUrl);
      return result;
    }

    function supportedFrequencies(selectedProducts) {
      if (!selectedProducts.length) return new Set(SUBSCRIPTION_FREQUENCIES);

      const intersections = selectedProducts.map(
        ({ product }) => new Set(product.sellingPlanMap.keys()),
      );
      const available = SUBSCRIPTION_FREQUENCIES.filter((label) =>
        intersections.every((options) => options.has(label)),
      );

      return new Set(available);
    }

    function getSubscriptionProducts(selectedProducts = getSelectedProducts()) {
      return selectedProducts.filter(
        ({ subscriptionQuantity }) => subscriptionQuantity > 0,
      );
    }

    function countsForSelection(selectedProducts) {
      return selectedProducts.reduce(
        (counts, entry) => {
          counts.total += entry.quantity;
          if (entry.product.category === "food") counts.food += entry.quantity;
          if (entry.product.category === "treat")
            counts.treat += entry.quantity;
          return counts;
        },
        { total: 0, food: 0, treat: 0 },
      );
    }

    function totalsForSelection(selectedProducts, tier) {
      return selectedProducts.reduce(
        (totals, entry) => {
          const originalLinePrice = Number(entry.product.price || 0) * entry.quantity;
          const finalLinePrice = tier?.discount
            ? applyDiscount(originalLinePrice, tier.discount)
            : originalLinePrice;

          totals.original += originalLinePrice;
          totals.final += finalLinePrice;
          return totals;
        },
        { original: 0, final: 0 },
      );
    }

    function renderFrequencyOptions(selectedProducts) {
      const availableOptions = supportedFrequencies(selectedProducts);
      if (
        state.subscriptionFrequency &&
        !availableOptions.has(state.subscriptionFrequency)
      ) {
        state.subscriptionFrequency = "";
      }

      frequencySelect.innerHTML = [
        '<option value="" selected>Select an interval</option>',
        ...SUBSCRIPTION_FREQUENCIES.map(
          (label) =>
            `<option value="${label}" ${label === state.subscriptionFrequency ? "selected" : ""} ${
              availableOptions.has(label) ? "" : "disabled"
            }>${label}</option>`,
        ),
      ].join("");
    }

    function updateCards() {
      const counts = countsForSelection(getSelectedProducts());
      const tier = getTier(counts.total, config.tiers || []);

      cards.forEach((card, handle) => {
        const subscriptionQuantity = state.subscriptionItems.get(handle) || 0;
        const oneTimeQuantity = state.oneTimeItems.get(handle) || 0;
        const controls = card.querySelectorAll(
          "[data-bundle-card-purchase-control]",
        );
        const priceNode = card.querySelector(".bundle-builder-card-price");
        const product = productMap.get(handle);

        if (priceNode && product) {
          if (tier?.discount) {
            const discountedPrice = applyDiscount(product.price, tier.discount);
            priceNode.innerHTML = `
              <span class="bundle-builder-card-price-current">${formatMoney(discountedPrice)}</span>
              <span class="bundle-builder-card-price-original">${formatMoney(product.price)}</span>
            `;
          } else {
            priceNode.textContent = formatMoney(product.price);
          }
        }

        controls.forEach((control) => {
          const type = control.dataset.bundleCardPurchaseControl;
          const quantity =
            type === "onetime" ? oneTimeQuantity : subscriptionQuantity;
          const addTrigger = control.querySelector(
            type === "onetime"
              ? "[data-bundle-add-onetime-trigger]"
              : "[data-bundle-add-subscription-trigger]",
          );
          const minusButton = control.querySelector(
            type === "onetime"
              ? "[data-card-onetime-minus]"
              : "[data-card-subscription-minus]",
          );
          const plusButton = control.querySelector(
            type === "onetime"
              ? "[data-card-onetime-plus]"
              : "[data-card-subscription-plus]",
          );
          const quantityInput = control.querySelector(
            type === "onetime"
              ? "[data-card-onetime-quantity]"
              : "[data-card-subscription-quantity]",
          );

          control.classList.toggle("is-active", quantity > 0);

          if (addTrigger) {
            addTrigger.hidden = quantity > 0;
            addTrigger.disabled = state.loading;
          }

          if (minusButton) minusButton.hidden = quantity <= 0;
          if (plusButton) plusButton.hidden = quantity <= 0;

          if (quantityInput) {
            quantityInput.hidden = quantity <= 0;
            quantityInput.value = quantity;
            quantityInput.disabled = state.loading;
          }
        });
      });
    }

    function renderSummaryItem({ product, quantity, type, tier }) {
      const discountedPrice = tier?.discount
        ? applyDiscount(product.price, tier.discount)
        : null;
      const priceMarkup = discountedPrice
        ? `<span class="bundle-builder-item-price-current">${formatMoney(discountedPrice)}</span> <span class="bundle-builder-item-price-original">${formatMoney(product.price)}</span>`
        : `${formatMoney(product.price)}`;

      return `
        <div class="bundle-builder-item">
          <img class="bundle-builder-item-image" src="${product.image || ""}" alt="${product.title}">
          <div class="bundle-builder-item-content">
            <div class="bundle-builder-item-head">
              <p class="bundle-builder-item-title">${product.title}</p>
              <button type="button" class="bundle-builder-item-remove" data-summary-${type}-remove="${product.handle}" aria-label="Remove ${product.title}">×</button>
            </div>
            <div class="bundle-builder-item-meta">
              <span>${priceMarkup} each</span>
            </div>
            <div class="bundle-builder-qty">
              <button type="button" data-summary-${type}-minus="${product.handle}">−</button>
              <span>${quantity}</span>
              <button type="button" data-summary-${type}-plus="${product.handle}">+</button>
            </div>
          </div>
        </div>
      `;
    }

    function getSummaryItems(selectedProducts, tier) {
      const subscriptionItems = selectedProducts
        .filter(({ subscriptionQuantity }) => subscriptionQuantity > 0)
        .map(({ product, subscriptionQuantity }) =>
          renderSummaryItem({
            product,
            quantity: subscriptionQuantity,
            type: "subscription",
            tier,
          }),
        )
        .join("");
      const oneTimeItems = selectedProducts
        .filter(({ oneTimeQuantity }) => oneTimeQuantity > 0)
        .map(({ product, oneTimeQuantity }) =>
          renderSummaryItem({
            product,
            quantity: oneTimeQuantity,
            type: "onetime",
            tier,
          }),
        )
        .join("");

      return [
        subscriptionItems
          ? `<div class="bundle-builder-summary-group"><p class="bundle-builder-summary-heading">Subscription</p>${subscriptionItems}</div>`
          : "",
        oneTimeItems
          ? `<div class="bundle-builder-summary-group"><p class="bundle-builder-summary-heading">One time</p>${oneTimeItems}</div>`
          : "",
      ].join("");
    }

    function setMessage(text, tone = "") {
      if (!text) {
        message.textContent = "";
        message.className = "bundle-builder-message";
        return;
      }

      message.textContent = text;
      message.className = `bundle-builder-message${tone ? ` is-${tone}` : ""}`;
    }

    function renderSummary() {
      const selectedProducts = getSelectedProducts();
      const counts = countsForSelection(selectedProducts);
      const tier = getTier(counts.total, config.tiers || []);
      const totals = totalsForSelection(selectedProducts, tier);
      const subscriptionProducts = getSubscriptionProducts(selectedProducts);

      renderFrequencyOptions(subscriptionProducts);
      updateCards();

      if (totalCount) {
        totalCount.textContent = `${counts.total}/${config.foodMinimum}`;
      }
      thresholdContainer.innerHTML = createThresholdMarkup(
        counts.total,
        config.tiers || [],
      );

      if (!selectedProducts.length) {
        summaryItems.innerHTML =
          '<p class="bundle-builder-empty">Select products to start building your order.</p>';
      } else {
        summaryItems.innerHTML = getSummaryItems(selectedProducts, tier);
      }

      const hasItems = counts.total > 0;
      const hasSubscriptionItems = subscriptionProducts.length > 0;
      const foodBelowMinimum =
        counts.food > 0 && counts.food < Number(config.foodMinimum || 4);
      const availableFrequencies = supportedFrequencies(subscriptionProducts);
      const hasSubscriptionSelection = Boolean(state.subscriptionFrequency);
      const invalidFrequency =
        hasSubscriptionSelection &&
        !availableFrequencies.has(state.subscriptionFrequency);
      const needsFrequency =
        hasSubscriptionItems &&
        !hasSubscriptionSelection &&
        availableFrequencies.size;
      const canAddOneTime =
        hasItems &&
        !foodBelowMinimum &&
        !needsFrequency &&
        !invalidFrequency &&
        !state.loading;

      let statusMessage = "Build a mix that works for your pet.";
      let statusTone = "";

      if (!hasItems) {
        statusMessage =
          "Select at least one product to add your bundle to the cart.";
      } else if (foodBelowMinimum) {
        statusMessage = `Add at least ${config.foodMinimum} food pouches to check out with food in the order.`;
        statusTone = "error";
      } else if (invalidFrequency) {
        statusMessage =
          "One or more selected products do not support the chosen subscription frequency.";
        statusTone = "error";
      } else if (needsFrequency) {
        statusMessage = "Select your subscription interval.";
        statusTone = "error";
      } else if (tier) {
        statusMessage = `${tier.discount}% off unlocked at ${tier.threshold} items${tier.freeShipping ? " and free shipping is included for subscriptions." : "."}`;
      } else if (counts.total < 8) {
        statusMessage = `Add ${8 - counts.total} more eligible item${8 - counts.total === 1 ? "" : "s"} to unlock 5% off.`;
      }

      setMessage(statusMessage, statusTone);

      if (subtotal && subtotalPrice) {
        subtotal.hidden = !hasItems;
        subtotalPrice.innerHTML =
          totals.original > totals.final
            ? `<s>${formatMoney(totals.original)}</s><span>${formatMoney(totals.final)}</span>`
            : `<span>${formatMoney(totals.final)}</span>`;
      }

      frequencySelect
        .closest(".bundle-builder-purchase")
        ?.classList.toggle(
          "is-error",
          Boolean(needsFrequency || invalidFrequency),
        );
      oneTimeButton.disabled = !canAddOneTime;

      const oneTimeLabel = oneTimeButton?.querySelector(".push-btn-surface");
      if (oneTimeLabel) {
        if (state.loading) {
          oneTimeLabel.textContent = "Adding…";
        } else {
          oneTimeLabel.textContent = "Add bundle to cart";
        }
      }
    }

    function updateQuantity(handle, delta, type = "subscription") {
      const targetItems =
        type === "onetime" ? state.oneTimeItems : state.subscriptionItems;
      const currentValue = targetItems.get(handle) || 0;
      const nextValue = currentValue + delta;

      setQuantity(handle, nextValue, type);
    }

    function setQuantity(handle, value, type = "subscription") {
      const targetItems =
        type === "onetime" ? state.oneTimeItems : state.subscriptionItems;
      const nextValue = Math.max(0, Number(value || 0));

      if (nextValue <= 0) {
        targetItems.delete(handle);
      } else {
        targetItems.set(handle, nextValue);
      }

      saveDraft();
      renderSummary();
    }

    function removeQuantity(handle, type = "subscription") {
      const targetItems =
        type === "onetime" ? state.oneTimeItems : state.subscriptionItems;
      targetItems.delete(handle);
      saveDraft();
      renderSummary();
    }

    async function removeEditedBundleLines() {
      if (!state.editKeys.length) return;

      const updates = state.editKeys.reduce((nextUpdates, key) => {
        nextUpdates[key] = 0;
        return nextUpdates;
      }, {});

      const response = await fetch(theme.routes.cart_update_url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ updates }),
      });

      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(
          data.description || data.message || "Unable to update the existing bundle.",
        );
      }
    }

    async function addBundleToCart(mode = "onetime") {
      const selectedProducts = getSelectedProducts();
      const counts = countsForSelection(selectedProducts);
      if (!selectedProducts.length) return;
      if (counts.food > 0 && counts.food < Number(config.foodMinimum || 4))
        return;
      if (
        getSubscriptionProducts(selectedProducts).length &&
        !state.subscriptionFrequency
      ) {
        setMessage("Select your subscription interval.", "error");
        frequencySelect
          .closest(".bundle-builder-purchase")
          ?.classList.add("is-error");
        return;
      }

      state.mode = mode;
      state.loading = true;
      renderSummary();

      const itemsWithoutSellingPlan = selectedProducts.filter(
        ({ product, subscriptionQuantity }) =>
          subscriptionQuantity > 0 &&
          !product.sellingPlanMap.get(state.subscriptionFrequency),
      );
      if (itemsWithoutSellingPlan.length) {
        state.loading = false;
        renderSummary();
        setMessage(
          "One or more selected products do not support the chosen subscription frequency.",
          "error",
        );
        return;
      }

      try {
        const loopDiscountQuantity = selectedProducts.reduce(
          (quantity, { subscriptionQuantity, oneTimeQuantity }) =>
            quantity + subscriptionQuantity + oneTimeQuantity,
          0,
        );
        const subscriptionProducts = getSubscriptionProducts(selectedProducts);
        const loopBundleId = await createLoopBundleTransaction(
          loopDiscountQuantity,
          subscriptionProducts.length ? state.subscriptionFrequency : null,
        );
        const items = selectedProducts.flatMap(
          ({ product, subscriptionQuantity, oneTimeQuantity }) => {
            const nextItems = [];
            const baseProperties = {
              _bundleBuilder: "true",
              _bundleLabel: "Build your order",
              _bundleCategory: product.category,
            };

            if (subscriptionQuantity > 0) {
              const sellingPlan = product.sellingPlanMap.get(
                state.subscriptionFrequency,
              );
              nextItems.push({
                id: product.variantId,
                quantity: subscriptionQuantity,
                selling_plan: sellingPlan?.id,
                properties: {
                  ...baseProperties,
                  _bundlePurchaseType: "subscription",
                  ...(loopBundleId
                    ? {
                        _bundleId: loopBundleId,
                        _bundleName: LOOP_BUNDLE_CONFIG.bundleName,
                      }
                    : {}),
                },
              });
            }

            if (oneTimeQuantity > 0) {
              nextItems.push({
                id: product.variantId,
                quantity: oneTimeQuantity,
                properties: {
                  ...baseProperties,
                  _bundlePurchaseType: "one_time",
                  ...(loopBundleId
                    ? {
                        _bundleId: loopBundleId,
                        _bundleName: LOOP_BUNDLE_CONFIG.bundleName,
                      }
                    : {}),
                },
              });
            }

            return nextItems;
          },
        );

        await removeEditedBundleLines();

        const response = await fetch(theme.routes.cart_add_url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({
            items,
            sections: "cart-items,cart-footer,cart-item-count",
            sections_url: window.location.pathname,
          }),
        });

        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(
            data.description || data.message || "Unable to add bundle to cart.",
          );
        }

        state.subscriptionItems.clear();
        state.oneTimeItems.clear();
        state.subscriptionFrequency = "";
        clearBundleDraft();
        renderSummary();
        setMessage("Bundle added to cart.", "success");

        const loopDiscountCode = loopBundleId
          ? getLoopDiscountCode(loopDiscountQuantity)
          : null;
        window.location.href = loopDiscountCode
          ? `/discount/${loopDiscountCode}?redirect=${encodeURIComponent(theme.routes.cart_url)}`
          : theme.routes.cart_url;
      } catch (error) {
        setMessage(
          error.message || "Unable to add the bundle to the cart.",
          "error",
        );
      } finally {
        state.loading = false;
        state.mode = "onetime";
        renderSummary();
      }
    }

    root.addEventListener("click", (event) => {
      const addSubscriptionTrigger = event.target.closest(
        "[data-bundle-add-subscription-trigger]",
      );
      if (addSubscriptionTrigger) {
        updateQuantity(
          addSubscriptionTrigger.dataset.productHandle,
          1,
          "subscription",
        );
        return;
      }

      const addOneTimeTrigger = event.target.closest(
        "[data-bundle-add-onetime-trigger]",
      );
      if (addOneTimeTrigger) {
        updateQuantity(addOneTimeTrigger.dataset.productHandle, 1, "onetime");
        return;
      }

      const cardSubscriptionPlusButton = event.target.closest(
        "[data-card-subscription-plus]",
      );
      if (cardSubscriptionPlusButton) {
        updateQuantity(
          cardSubscriptionPlusButton.dataset.cardSubscriptionPlus,
          1,
          "subscription",
        );
        return;
      }

      const cardSubscriptionMinusButton = event.target.closest(
        "[data-card-subscription-minus]",
      );
      if (cardSubscriptionMinusButton) {
        updateQuantity(
          cardSubscriptionMinusButton.dataset.cardSubscriptionMinus,
          -1,
          "subscription",
        );
        return;
      }

      const cardOneTimePlusButton = event.target.closest(
        "[data-card-onetime-plus]",
      );
      if (cardOneTimePlusButton) {
        updateQuantity(
          cardOneTimePlusButton.dataset.cardOnetimePlus,
          1,
          "onetime",
        );
        return;
      }

      const cardOneTimeMinusButton = event.target.closest(
        "[data-card-onetime-minus]",
      );
      if (cardOneTimeMinusButton) {
        updateQuantity(
          cardOneTimeMinusButton.dataset.cardOnetimeMinus,
          -1,
          "onetime",
        );
        return;
      }

      const subscriptionSummaryPlus = event.target.closest(
        "[data-summary-subscription-plus]",
      );
      if (subscriptionSummaryPlus) {
        updateQuantity(
          subscriptionSummaryPlus.dataset.summarySubscriptionPlus,
          1,
          "subscription",
        );
        return;
      }

      const subscriptionSummaryMinus = event.target.closest(
        "[data-summary-subscription-minus]",
      );
      if (subscriptionSummaryMinus) {
        updateQuantity(
          subscriptionSummaryMinus.dataset.summarySubscriptionMinus,
          -1,
          "subscription",
        );
        return;
      }

      const oneTimeSummaryPlus = event.target.closest(
        "[data-summary-onetime-plus]",
      );
      if (oneTimeSummaryPlus) {
        updateQuantity(
          oneTimeSummaryPlus.dataset.summaryOnetimePlus,
          1,
          "onetime",
        );
        return;
      }

      const oneTimeSummaryMinus = event.target.closest(
        "[data-summary-onetime-minus]",
      );
      if (oneTimeSummaryMinus) {
        updateQuantity(
          oneTimeSummaryMinus.dataset.summaryOnetimeMinus,
          -1,
          "onetime",
        );
        return;
      }

      const subscriptionSummaryRemove = event.target.closest(
        "[data-summary-subscription-remove]",
      );
      if (subscriptionSummaryRemove) {
        removeQuantity(
          subscriptionSummaryRemove.dataset.summarySubscriptionRemove,
          "subscription",
        );
        return;
      }

      const oneTimeSummaryRemove = event.target.closest(
        "[data-summary-onetime-remove]",
      );
      if (oneTimeSummaryRemove) {
        removeQuantity(
          oneTimeSummaryRemove.dataset.summaryOnetimeRemove,
          "onetime",
        );
        return;
      }

      if (event.target.closest("[data-bundle-submit-onetime]")) {
        addBundleToCart("onetime");
        return;
      }
    });

    root.addEventListener("change", (event) => {
      const subscriptionInput = event.target.closest(
        "[data-card-subscription-quantity]",
      );
      if (subscriptionInput) {
        setQuantity(
          subscriptionInput.dataset.cardSubscriptionQuantity,
          subscriptionInput.value,
          "subscription",
        );
        return;
      }

      const oneTimeInput = event.target.closest("[data-card-onetime-quantity]");
      if (oneTimeInput) {
        setQuantity(
          oneTimeInput.dataset.cardOnetimeQuantity,
          oneTimeInput.value,
          "onetime",
        );
        return;
      }
    });

    frequencySelect.addEventListener("change", (event) => {
      state.subscriptionFrequency = event.target.value;
      saveDraft();
      renderSummary();
    });

    cards.forEach((card) => {
      const hoverCarouselCount = Number(card.dataset.hoverCarouselCount || 0);
      if (hoverCarouselCount < 1) return;

      setCardSlide(card, 0);

      card.addEventListener("mouseenter", () => {
        if (card.dataset.manualSlide === "true") return;
        setCardSlide(card, 1);
      });

      card.addEventListener("mouseleave", () => {
        if (card.dataset.manualSlide === "true") return;
        setCardSlide(card, 0);
      });

      card.querySelectorAll("[data-bundle-card-dot]").forEach((dot) => {
        dot.addEventListener("click", () => {
          card.dataset.manualSlide = "true";
          setCardSlide(card, Number(dot.dataset.slideIndex || 0));
        });
      });
    });

    hydrateDraft();
    const incomingMessage = mergeIncomingBundleItem();
    renderSummary();
    if (incomingMessage) {
      setMessage(incomingMessage.text, incomingMessage.tone);
    }
  });
});
